import { useQuery } from "@tanstack/react-query";
import type { ProfilePage } from "@amber/application/contracts";
import { appearancesQuery } from "@amber/profiles/ui";
import { AppLink, formatDate } from "@amber/ui";

export function ItemsTab({ profile }: { profile: ProfilePage }) {
  const data = useQuery(appearancesQuery(profile.id)).data;
  if (!data) return <p className="px-4 text-md text-muted">Loading…</p>;
  const pct = (n: number | null) => (n === null ? "no score" : n.toFixed(2));
  const list = (rows: typeof data.items, label: string) => (
    <ul aria-label={label} className="m-0 mt-1.5 list-none p-0">
      {rows.map((a) => (
        <li
          key={a.item_id}
          className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_110px_120px] items-center gap-3 border-t border-divider py-2"
        >
          <AppLink href={a.href} className="truncate font-strong">
            {a.title}
          </AppLink>
          <span className="truncate text-md text-muted">{a.source ?? "Added by link"}</span>
          <span className="text-md text-muted">{a.published ? formatDate(a.published) : "Date unknown"}</span>
          <span className="font-mono text-md">attribution {pct(a.confidence)}</span>
        </li>
      ))}
    </ul>
  );
  return (
    <div className="overflow-auto px-4 pb-6 text-ui">
      <p className="m-0 mt-1">
        Appears as speaker in <b className="font-mono">{data.count}</b> item
        {data.count === 1 ? "" : "s"} from sources outside the plan ({data.communications} communication
        {data.communications === 1 ? "" : "s"}), counted from attribution confidence {data.threshold.toFixed(2)}.{" "}
        <AppLink href={`/backlog?person=${profile.id}`} className="text-link underline">
          Open the backlog for {profile.name}
        </AppLink>
      </p>
      {data.items.length > 0 ? (
        list(data.items, "Counted appearances")
      ) : (
        <p className="m-0 mt-2 text-md text-muted">No counted appearances.</p>
      )}
      {data.possible.length > 0 ? (
        <>
          <h2 className="m-0 mt-5 text-xs font-semibold tracking-[0.06em] text-muted uppercase">
            Possible appearances{" "}
            <span className="font-normal normal-case tracking-normal">
              below {data.threshold.toFixed(2)}, not counted
            </span>
          </h2>
          {list(data.possible, "Possible appearances")}
        </>
      ) : null}
      <p className="m-0 mt-4 text-md text-muted">Appearances don't establish monitoring or coverage.</p>
    </div>
  );
}
